<script setup lang="ts">
import type { ThemeSettings } from '~/stores/auth'
import { DEFAULT_THEME_SETTINGS } from '~/stores/auth'
import type { AudioPlayerState } from '~/composables/useAudioPlayer'

const props = defineProps<{
  hasAudio: boolean
  showPlayer: boolean
  showBranding: boolean
  isOwner: boolean
  showViews: boolean
  totalViews?: number
  themeSettings: ThemeSettings
}>()

// Inject shared audio state
const ap = inject<AudioPlayerState | null>('audioPlayer', null)

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return [r, g, b]
}

const primaryRgb = computed(() => hexToRgb(props.themeSettings.primaryColor))
const primaryLight = computed(() => `color-mix(in srgb, ${props.themeSettings.primaryColor} 50%, white)`)

const barStyle = computed(() => {
  const theme = props.themeSettings ?? DEFAULT_THEME_SETTINGS
  const rgb = hexToRgb(theme.cardColor)
  return {
    background: `rgba(${rgb.join(',')}, ${Math.max(theme.cardOpacity / 100, 0.7)})`,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderColor: `rgba(255, 255, 255, 0.08)`
  }
})
</script>

<template>
  <!-- Mobile-only consolidated bottom bar. Rendered server-side so the
       branding link (the page's only internal link) is in the SSR HTML; the
       slide-in runs via the Transition's `appear`. -->
  <Transition
    name="bar-slide"
    appear
  >
    <div
      class="fixed bottom-0 inset-x-0 z-50 sm:hidden border-t"
      :style="barStyle"
    >
      <div class="flex items-center gap-2 px-3 py-2.5 safe-bottom">
        <!-- Audio controls -->
        <template v-if="hasAudio && ap">
          <button
            class="size-8 shrink-0 flex items-center justify-center rounded-full bg-primary-500/20 border border-primary-400/30 text-primary-300 active:bg-primary-500/40 transition-all cursor-pointer"
            @click="ap.togglePlay"
          >
            <UIcon
              :name="ap.isPlaying.value ? 'i-lucide-pause' : 'i-lucide-play'"
              class="size-3.5"
            />
          </button>

          <!-- Track name (truncated) -->
          <p
            v-if="showPlayer && ap.currentTrack.value"
            class="text-[11px] text-white/50 truncate min-w-0 flex-1"
          >
            {{ ap.currentTrack.value.original_name }}
          </p>

          <!-- Volume toggle -->
          <button
            class="size-6 shrink-0 flex items-center justify-center text-white/40 active:text-white/70 transition-colors cursor-pointer"
            @click="ap.toggleMute"
          >
            <UIcon
              :name="ap.volumeIcon.value"
              class="size-3"
            />
          </button>

          <!-- Divider -->
          <div class="w-px h-4 bg-white/10 shrink-0" />
        </template>

        <!-- Spacer when no audio -->
        <div
          v-if="!hasAudio"
          class="flex-1"
        />

        <!-- Branding -->
        <NuxtLink
          v-if="showBranding"
          :to="isOwner ? '/pricing' : '/'"
          class="flex items-center gap-1.5 shrink-0"
        >
          <img
            src="/logo_short_transparent_white.png"
            alt="mychar"
            class="size-3.5"
          >
          <span
            class="text-[11px] font-bold tracking-wide"
            :style="{ color: primaryLight }"
          >
            mychar.gg
          </span>
        </NuxtLink>

        <!-- Spacer -->
        <div class="flex-1" />

        <!-- Views counter -->
        <div
          v-if="showViews && totalViews !== undefined"
          class="flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full"
          :style="{
            background: `rgba(${primaryRgb.join(',')}, 0.1)`,
            border: `1px solid rgba(${primaryRgb.join(',')}, 0.15)`
          }"
        >
          <UIcon
            name="i-lucide-eye"
            class="size-3"
            :style="{ color: primaryLight }"
          />
          <span
            class="text-[11px] font-bold tabular-nums"
            :style="{ color: primaryLight }"
          >
            {{ totalViews.toLocaleString() }}
          </span>
        </div>

        <!-- FABs (passed from page) -->
        <slot name="fabs" />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.safe-bottom {
  padding-bottom: max(0.625rem, env(safe-area-inset-bottom));
}

.bar-slide-enter-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 0.8s;
}
.bar-slide-leave-active {
  transition: all 0.3s ease-in;
}
.bar-slide-enter-from {
  opacity: 0;
  transform: translateY(100%);
}
.bar-slide-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
